import { useEffect, useState } from 'react';
import { AppDialog } from '../../../components/AppDialog';
import { useNotify } from '../../../components/notifications';
import { useSaveFlow } from '../../../components/confirm';
import { createNamedLookup, renameNamedLookup } from './detailApi';
import type { NamedLookupKind } from './detailTypes';

const CAPTION: Record<NamedLookupKind, { title: string; field: string }> = {
  'payment-method':     { title: 'Payment Method',     field: 'Payment' },
  credit:               { title: 'Credit',             field: 'Credits' },
  'source-opportunity': { title: 'Source Opportunity', field: 'Opportunity' },
};

/**
 * The New / Edit popup behind the Payment Method, Credit and Source Opportunity
 * lookups — legacy's lookup New and Edit buttons open the master's one-field
 * detail view. The server checks the name (required, unique) and audits it.
 */
export function QuoteNamedLookupDialog({
  target, onClose, onSaved,
}: {
  /** Which list, and the row to rename (null id = New). */
  target: { kind: NamedLookupKind; id: string | null; name: string | null } | null;
  onClose: () => void;
  onSaved: (id: string) => void;
}) {
  const notify = useNotify();
  const runSave = useSaveFlow();
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => { setName(target?.name ?? ''); }, [target]);

  if (!target) return null;
  const caption = CAPTION[target.kind];

  async function save() {
    if (!target || saving) return;
    if (!name.trim()) { notify.warning(`${caption.title} must not be empty.`); return; }
    // The shared save flow (standing rule 7): "Do you want to Save?" → save →
    // "Saved successfully.". The quote behind keeps its own unsaved guard
    // (useUnsavedGuard), which this flow's tracker reset does not clear.
    setSaving(true);
    const saved: { id: string | null } = { id: null };
    try {
      await runSave({
        save: async () => {
          saved.id = target.id
            ? await renameNamedLookup(target.kind, target.id, name.trim())
            : await createNamedLookup(target.kind, name.trim());
        },
        // A refusal (duplicate name, …) shows the server's own message — the
        // flow's default error handling.
        successAs: 'toast',
      });
    } finally {
      setSaving(false);
    }
    if (saved.id) onSaved(saved.id);
  }

  return (
    <AppDialog
      open
      title={target.id ? `${caption.title} – ${target.name ?? ''}` : `New ${caption.title}`}
      width={460}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="ca-btn ca-btn-solid" onClick={() => void save()} disabled={saving}>OK</button>
          <button type="button" className="ca-btn" onClick={onClose}>Cancel</button>
        </>
      }
    >
      <div className="sd-grid sd-grid--single sd-grid--side">
        <div className="sd-row">
          <label className="sd-label" htmlFor="qt-named">{caption.field}<span className="req">*</span></label>
          <input id="qt-named" type="text" maxLength={100} autoComplete="off"
                 className={`sd-input${name.trim() ? '' : ' required-empty'}`}
                 value={name} onChange={(e) => setName(e.target.value)} />
        </div>
      </div>
    </AppDialog>
  );
}
